import { h, type App } from 'vue';
import { Wormhole } from 'portal-vue';
import {
  uniqueID,
  type INotification,
  type NotificationOptions,
} from '@integro/core';
import debug from 'debug';

const log = debug('itp:ui:components:notification:plugin');
const PORTAL_TARGET = 'notification';
const createId = () => `notification-${uniqueID()}`;

interface NotificationInstance {
  id: string;
  resolve: (value: void | PromiseLike<void>) => void;
  reject: (reason?: unknown) => void;
}

export class NotificationManager {
  private instances: NotificationInstance[] = [];
  private app: App | null = null;

  setApp(app: App) {
    this.app = app;
  }

  show(options: NotificationOptions): Promise<void> {
    const id = createId();
    log('show %o, id: %s', options, id);

    return new Promise<void>((resolve, reject) => {
      const vnode = h('ItpNotification', {
        ...options,
        uid: id,
        onRemove: () => this.close(id),
      });

      if (this.app) {
        vnode.appContext = this.app._context;
      }

      Wormhole.open({
        to: PORTAL_TARGET,
        from: id,
        content: () => [vnode],
        order: this.instances.length + 1,
      });

      this.instances.push({ id, resolve, reject });
    });
  }

  close(id: string) {
    log('close %s', id);

    const instance = this.instances.find((inst) => inst.id === id);

    if (!instance) {
      console.warn(`Notification instance "${id}" not found.`);
      return;
    }

    Wormhole.close({ from: id, to: PORTAL_TARGET });

    this.instances = this.instances.filter((inst) => inst.id !== id);
    instance.resolve();
  }
}

export class NotificationService implements INotification {
  private manager: NotificationManager;

  constructor(manager: NotificationManager) {
    this.manager = manager;
  }

  show(options: NotificationOptions): Promise<void> {
    return this.manager.show(options);
  }

  close(id: string) {
    return this.manager.close(id);
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $notification: NotificationService;
  }
}

export function createNotificationPlugin() {
  const manager = new NotificationManager();
  const service = new NotificationService(manager);

  return {
    install(app: App) {
      manager.setApp(app);
      app.config.globalProperties.$notification = service;
    },
    service,
  };
}
